class StickyProductBar extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.onScroll = () => {
      if (!this.visibilityFrame) this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    };
    queueMicrotask(() => this.connect());
  }

  connect() {
    if (!this.isConnected) return;
    this.form = document.getElementById(this.dataset.formId);
    this.section = this.closest('.shopify-section');
    this.info = this.section?.querySelector('.product-info');
    if (!this.form || !this.info) return;
    this.scrollMode = this.dataset.scrollToOptions === 'true';
    const signal = this.abort.signal;
    window.addEventListener('scroll', this.onScroll, { passive: true, signal });
    window.addEventListener('resize', this.onScroll, { passive: true, signal });
    this.querySelector('.sticky-product-bar__title').addEventListener('click', event => this.scrollToOptions(event), { signal });
    if (this.scrollMode) this.querySelector('button').addEventListener('click', event => this.scrollToOptions(event), { signal });
    this.form.addEventListener('cart:error', event => {
      const error = this.querySelector('.sticky-product-bar__error');
      error.textContent = event.detail.error;
      error.hidden = false;
    }, { signal });
    this.form.addEventListener('variant:add', () => { this.querySelector('.sticky-product-bar__error').hidden = true; }, { signal });
    this.info.addEventListener('change', () => this.scheduleSync(), { signal });
    // Kaching updates its own fields asynchronously; observe only the hero,
    // never the bar itself, to avoid mutation loops.
    this.observer = new MutationObserver(() => this.scheduleSync());
    this.observer.observe(this.info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-busy', 'hidden', 'src'] });
    this.sync();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.syncFrame);
    cancelAnimationFrame(this.visibilityFrame);
  }

  scheduleSync() {
    if (this.syncFrame) return;
    this.syncFrame = requestAnimationFrame(() => { this.syncFrame = null; this.sync(); this.updateVisibility(); });
  }

  updateVisibility() {
    if (!this.form) return;
    const target = this.form.querySelector('button[type="submit"]') || this.form;
    const visible = target.getBoundingClientRect().bottom < 0;
    this.classList.toggle('is-visible', visible);
    this.inert = !visible;
  }

  setText(selector, text) {
    const node = this.querySelector(selector);
    if (node && node.textContent !== text) node.textContent = text;
  }

  sync() {
    const mainButton = this.form.querySelector('button[type="submit"]');
    const button = this.querySelector('button');
    if (!this.scrollMode) {
      button.disabled = !mainButton || mainButton.disabled;
      button.toggleAttribute('aria-busy', mainButton?.getAttribute('aria-busy') === 'true');
      if (button.hasAttribute('aria-busy')) button.setAttribute('aria-busy', 'true');
      return;
    }
    const selected = this.info.querySelector('.kaching-bundles__bar--selected');
    const rows = selected ? [...selected.querySelectorAll('.kaching-bundles__bar-variant')] : [];
    const headline = this.info.querySelector('.product-info__price');
    const amount = selected?.querySelector('.kaching-bundles__bar-price')?.textContent.trim()
      || headline?.querySelector('sale-price [data-performance-set-add-price-suffix]')?.textContent.trim();
    const compare = selected?.querySelector('.performance-set-kaching__tier-compare')
      || headline?.querySelector('compare-at-price');
    if (amount && !this.info.classList.contains('is-kaching-loading')) {
      this.setText('[data-sticky-price]', amount);
      this.setText('[data-sticky-compare]', compare?.textContent.trim() || '');
      this.querySelector('[data-sticky-compare]').hidden = !compare || compare.hidden || !compare.textContent.trim();
      this.querySelector('.sticky-product-bar__prices').removeAttribute('data-await-bundle');
    }
    if (!rows.length) return;
    const fields = [...rows[0].querySelectorAll('select')];
    const values = fields.map(field => field.value).filter(Boolean);
    if (values.length) {
      const quantity = selected.dataset.performanceSetQuantity || rows.length;
      const label = values.join(' / ') + (Number(quantity) > 1 ? ` · +${Number(quantity) - 1}` : '');
      this.setText('.sticky-product-bar__variant', label);
      this.querySelector('.sticky-product-bar__selection').hidden = false;
      const swatch = rows[0].querySelector('.performance-set-kaching__color-swatch');
      if (swatch) {
        const target = this.querySelector('.sticky-product-bar__swatch');
        target.style.background = swatch.style.background;
        target.hidden = false;
      }
    }
    const image = rows[0].querySelector('img.kaching-bundles__bar-variant-image, .kaching-bundles__bar-variant-image img');
    const targetImage = this.querySelector('.sticky-product-bar__image');
    if (image && targetImage && targetImage.src !== image.src) {
      targetImage.removeAttribute('srcset');
      targetImage.src = image.src;
    }
  }

  scrollToOptions(event) {
    event.preventDefault();
    const target = this.info.querySelector('.product-info__title') || this.info;
    const header = document.querySelector('store-header, .shopify-section--header');
    const offset = Math.min(header?.getBoundingClientRect().height || 0, 160) + 20;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    const focusTarget = this.info.querySelector('h1') || this.info;
    if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
  }
}
if (!customElements.get('sticky-product-bar')) customElements.define('sticky-product-bar', StickyProductBar);
